<script setup lang="ts">
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import FairDialogPanel from './FairDialogPanel.vue'
import { FAIR_DIALOG_DEFAULT_WIDTH } from '@/ui/tokens/dialog.data'
import type { FairIconName } from '@/ui/tokens/icons.data'

interface FairDialogProps {
  visible?: boolean
  header?: string
  closable?: boolean
  showHeader?: boolean
  showFooter?: boolean
  showCancel?: boolean
  cancelLabel?: string
  confirmLabel?: string
  cancelLeadingIcon?: FairIconName
  cancelTrailingIcon?: FairIconName
  confirmLeadingIcon?: FairIconName
  confirmTrailingIcon?: FairIconName
  modal?: boolean
  dismissableMask?: boolean
  closeOnEscape?: boolean
  width?: string
  contentText?: string
}

const props = withDefaults(defineProps<FairDialogProps>(), {
  visible: false,
  header: 'Header',
  closable: true,
  showHeader: true,
  showFooter: true,
  showCancel: true,
  cancelLabel: 'Для себя',
  confirmLabel: 'Для себя',
  cancelLeadingIcon: undefined,
  cancelTrailingIcon: undefined,
  confirmLeadingIcon: undefined,
  confirmTrailingIcon: undefined,
  modal: true,
  dismissableMask: true,
  closeOnEscape: true,
  width: FAIR_DIALOG_DEFAULT_WIDTH,
  contentText: undefined,
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  close: []
  cancel: [event: MouseEvent]
  confirm: [event: MouseEvent]
}>()

const dialogStyle = computed(() => ({
  width: props.width,
}))

function setVisible(value: boolean) {
  emit('update:visible', value)
}

function handleClose() {
  setVisible(false)
  emit('close')
}

function handleCancel(event: MouseEvent) {
  emit('cancel', event)
}

function handleConfirm(event: MouseEvent) {
  emit('confirm', event)
}

function handleHide() {
  setVisible(false)
  emit('close')
}
</script>

<template>
  <Dialog
    :visible="visible"
    class="fair-dialog"
    :modal="modal"
    :dismissable-mask="dismissableMask"
    :closable="false"
    :close-on-escape="closeOnEscape"
    :show-header="false"
    :style="dialogStyle"
    :pt="{
      root: { class: 'fair-dialog' },
      mask: { class: 'fair-dialog-mask' },
    }"
    @update:visible="setVisible"
    @hide="handleHide"
  >
    <template #container="{ closeCallback }">
      <FairDialogPanel
        :header="header"
        :closable="closable"
        :show-header="showHeader"
        :show-footer="showFooter"
        :show-cancel="showCancel"
        :cancel-label="cancelLabel"
        :confirm-label="confirmLabel"
        :cancel-leading-icon="cancelLeadingIcon"
        :cancel-trailing-icon="cancelTrailingIcon"
        :confirm-leading-icon="confirmLeadingIcon"
        :confirm-trailing-icon="confirmTrailingIcon"
        :content-text="contentText"
        @close="closeCallback(); handleClose()"
        @cancel="handleCancel"
        @confirm="handleConfirm"
      >
        <template
          v-if="$slots['header-start']"
          #header-start
        >
          <slot name="header-start" />
        </template>
        <template
          v-if="$slots['header-end']"
          #header-end
        >
          <slot name="header-end" />
        </template>
        <template
          v-if="$slots.header"
          #header
        >
          <slot name="header" />
        </template>
        <template
          v-if="$slots.default"
          #default
        >
          <slot />
        </template>
        <template
          v-if="$slots.footer"
          #footer
        >
          <slot name="footer" />
        </template>
      </FairDialogPanel>
    </template>
  </Dialog>
</template>
